import { ZodIssueCode, array, boolean, object, z } from 'zod'

import { DocumentType, ProcedureType } from '@/api/fetchers.schemas'
import createEmptyObject from '@/utils/createEmptyObject'
import { schemaDefaults } from '@/validation/zodSchema'

export const SCHEMA_PUBLICATION = object({
    Module_ID: schemaDefaults.requiredNumber(),
    Title: schemaDefaults.requiredString(),
    Document_Type: z.nativeEnum(DocumentType),
    Procedure_Type: z.nativeEnum(ProcedureType),
    Template_UUID: schemaDefaults.requiredString(),
    Environment_UUID: schemaDefaults.requiredString(),
    Act_UUID: schemaDefaults.requiredString(),
    Module_Status_ID: schemaDefaults.requiredNumber(),
})

export const PUBLICATION_EDIT_SCHEMA = object({
    Template_UUID: schemaDefaults.requiredString(),
})

export const PUBLICATION_ADD_SCHEMA = object({
    Act_UUID: schemaDefaults.requiredString(),
    Template_UUID: schemaDefaults.requiredString(),
    Module_Status_ID: schemaDefaults.requiredNumber(),
})

export const PUBLICATION_VERSION_ADD_SCHEMA = object({
    Module_Status_ID: schemaDefaults.requiredNumber(),
})

export const PUBLICATION_VERSION_EDIT_SCHEMA = object({
    Module_Status_ID: schemaDefaults.requiredNumber(),
    Bill_Metadata: object({
        Official_Title: schemaDefaults.requiredString(),
        Quote_Title: schemaDefaults.requiredString(),
    }),
    Bill_Compact: object({
        Preamble: schemaDefaults.optionalRte(),
        Amendment_Article: schemaDefaults.requiredString(),
        Time_Article: schemaDefaults.rte(),
        Custom_Articles: array(
            object({
                Number: schemaDefaults.requiredString(),
                Content: schemaDefaults.rte(),
            })
        )
            .optional()
            .nullable(),
        Appendices: array(
            object({
                Number: schemaDefaults.requiredString(),
                Title: schemaDefaults.requiredString(),
                Content: schemaDefaults.rte(),
            })
        )
            .optional()
            .nullable(),
        Motivation: z.preprocess(
            value => {
                const motivation = value as {
                    Title?: string | null
                    Content?: string | null
                    Appendices?: unknown[] | null
                } | null

                const title = motivation?.Title?.trim()
                const content = motivation?.Content?.trim()
                const appendices = motivation?.Appendices ?? []

                if (!title && !content && appendices.length === 0) {
                    return null
                }

                return value
            },
            object({
                Title: schemaDefaults.requiredString(),
                Content: schemaDefaults.rte(),
                Appendices: array(
                    object({
                        Number: schemaDefaults.requiredString(),
                        Title: schemaDefaults.requiredString(),
                        Content: schemaDefaults.rte(),
                    })
                )
                    .optional()
                    .nullable(),
            })
                .optional()
                .nullable()
        ),
        Closing: schemaDefaults.optionalRte(),
        Signed: schemaDefaults.optionalRte(),
    }),
    Effective_Date: z.preprocess(
        value => (value === '' ? null : value),
        schemaDefaults.optionalString.nullable().refine(date => {
            return !date || new Date(date) > new Date()
        }, 'De inwerkingtredingsdatum moet in de toekomst liggen')
    ),
    Announcement_Date: schemaDefaults.optionalString
        .refine(date => {
            return !date || new Date(date) > new Date()
        }, 'De bekendmakingsdatum moet in de toekomst liggen')
        .nullable(),
    Procedural: object({
        Enactment_Date: schemaDefaults.optionalString,
        Signed_Date: schemaDefaults.optionalString,
        Procedural_Announcement_Date: schemaDefaults.optionalString,
    }),
}).superRefine(({ Effective_Date, Announcement_Date }, ctx) => {
    if (
        Effective_Date &&
        Announcement_Date &&
        new Date(Announcement_Date) >= new Date(Effective_Date)
    ) {
        ctx.addIssue({
            code: ZodIssueCode.custom,
            message:
                'De bekendmakingsdatum moet eerder zijn dan de inwerkingtredingsdatum',
            path: ['Announcement_Date'],
        })
    }
})

export const PUBLICATION_VERSION_ATTACHMENT_SCHEMA = object({
    title: schemaDefaults.requiredString(),
    uploaded_file: schemaDefaults.file,
    ignore_report: boolean().optional(),
})

export const EMPTY_PUBLICATION_OBJECT = createEmptyObject(SCHEMA_PUBLICATION)
